// utils/submissionUtils.js
import axios from './axios';

/**
 * Strip any suffix a manuscript ID may carry so we can key off the base ID.
 * Handles:
 *   ASFIRJ-2026-000209              → ASFIRJ-2026-000209
 *   ASFIRJ-2026-000209.R1           → ASFIRJ-2026-000209
 *   ASFIRJ-2026-000209.R1.Cr2       → ASFIRJ-2026-000209
 *   ASFIRJ-2026-000209.Cr3          → ASFIRJ-2026-000209
 *   ASFIRJ-2026-000209_rev1         → ASFIRJ-2026-000209
 *   ASFIRJ-2026-000209-correction-1 → ASFIRJ-2026-000209
 */
export const getBaseManuscriptId = (manuscriptId, type) => {
  if (!manuscriptId || typeof manuscriptId !== 'string') return '';
  // Match the canonical ASFIRJ-YYYY-NNNNNN prefix and drop everything after it.

  if(type === 'revision'){
  const match = manuscriptId.match(/^(ASFIRJ-\d{4}-\d{6})/i);
  if (match) return match[1];
  // STrip known sufixes for revisions: .R1, .R2, .rev1, .rev2, -revision-1, -revision-2
  return manuscriptId.replace(/([._-](R|Cr|rev|correction)\d*)+$/i, '').trim();
  }

  // Fallback for other formats: strip known suffixes.
  return manuscriptId.replace(/([._-](Cr|rev|correction)\d*)+$/i, '').trim();
};

const REVISION_TYPE = 'revision';
const CORRECTION_TYPE = 'correction';

/**
 * Safely fetch a count. Returns `fallback` on any error and logs once.
 */
const safeGetCount = async (fetcher, id, label) => {
  try {
    const count = await fetcher(id);
    return Number.isFinite(count) ? count : 0;
  } catch (err) {
    console.error(`Failed to fetch ${label} count for ${id}:`, err);
    return 0;
  }
};

export const getRevisionsCount = async (manuscriptId) => {
  const response = await axios.get(`/manuscript/${manuscriptId}/revisions/count`);
  return response.data?.success ? Number(response.data.count) || 0 : 0;
};

export const getCorrectionsCount = async (manuscriptId) => {
  const response = await axios.get(`/manuscript/${manuscriptId}/corrections/count`);
  return response.data?.success ? Number(response.data.count) || 0 : 0;
};

/**
 * Build the next revision/correction ID for a manuscript.
 * Always derived from the BASE id, never the raw id.
 */
export const buildNextId = (baseId, type, count) => {
  if (!baseId) throw new Error('Cannot build next ID without a base manuscript ID');
  const suffix = type === CORRECTION_TYPE ? 'Cr' : 'R';
  return `${baseId}.${suffix}${count + 1}`;
};

/**
 * Validate the type param. Throws on unknown values so callers fail loud.
 */
const normalizeType = (type) => {
  const t = String(type || '').toLowerCase().trim();
  if (t === REVISION_TYPE || t === 'revise' || t === 'revisions') return REVISION_TYPE;
  if (t === CORRECTION_TYPE || t === 'correct' || t === 'corrections') return CORRECTION_TYPE;
  throw new Error(`Unknown submission type: "${type}". Expected "revision" or "correction".`);
};

/**
 * Navigate with a full reload. Kept as a fallback for non-SPA contexts.
 */
const hardNavigate = (url) => {
  window.location.href = url;
};

/**
 * Open the submission wizard for a new revision or correction.
 *
 * @param {string} manuscriptId - The ID of the manuscript the user clicked.
 * @param {'revision'|'correction'} type
 * @param {object} [options]
 * @param {(url: string) => void} [options.navigate] - Router-aware navigator. Defaults to window.location.
 * @returns {Promise<{newManuscriptId: string, baseManuscriptId: string, url: string}>}
 */
export const startRevisionOrCorrection = async (manuscriptId, type, options = {}) => {
  const navigate = options.navigate || hardNavigate;
  const normalizedType = normalizeType(type);

  const baseManuscriptId = getBaseManuscriptId(manuscriptId, normalizedType);
  if (!baseManuscriptId) {
    throw new Error(`Invalid manuscript ID: "${manuscriptId}"`);
  }

  let nextCount;
  if (normalizedType === CORRECTION_TYPE) {
    nextCount = await safeGetCount(getCorrectionsCount, baseManuscriptId, CORRECTION_TYPE);
  } else {
    nextCount = await safeGetCount(getRevisionsCount, baseManuscriptId, REVISION_TYPE);
  }

  const newManuscriptId = buildNextId(baseManuscriptId, normalizedType, nextCount);
  const StripRevisionSuffix = (id) => id.replace(/([._-](R|Cr|rev|correction)\d*)+$/i, '').trim();
 
  const params = new URLSearchParams({
    [normalizedType]: 'true',   // ?revision=true  or  ?correction=true
    a: newManuscriptId,
    pid: normalizedType === CORRECTION_TYPE ? baseManuscriptId : StripRevisionSuffix(baseManuscriptId),
  });
  if(normalizedType === REVISION_TYPE) {
    params.append('rid', manuscriptId); // Pass the original revision ID for context
  }

  const url = `/submit?${params.toString()}`;
  navigate(url);

  return { newManuscriptId, baseManuscriptId, url };
};

// Convenience wrappers — nice for call sites that know which flow they're in.
export const handleStartRevision  = (manuscriptId, options) =>
  startRevisionOrCorrection(manuscriptId, REVISION_TYPE, options);

export const handleStartCorrection = (manuscriptId, options) =>
  startRevisionOrCorrection(manuscriptId, CORRECTION_TYPE, options);